import FalconComponentCard from "components/common/FalconComponentCard";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Row, Col, Form } from "react-bootstrap";
import PreauthorizationListFilter from "./PreauthorizationListFilter";
import { useForm } from "react-hook-form";
import PreauthorizationListTable from "./PreauthorizationListTable";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getToday } from "helpers/utils";
import { GetAllPreauthorizationList } from "../config/preauthorizationApi";
import {
  handleResetAll,
  handleResetPreauthorizationFilter,
} from "../config/preauthorizationSlice";
import { handleDownloadXLSX } from "helpers/excel";
import { handleResetPatientSearch } from "features/eliglibility/eliglibility-check/config/eligibilityCheckSlice";
import {
  handlePreauthFormData,
  handleResetClaimAll,
  handleResetCreateClaim,
} from "features/cliams/config/claimSlice";
import { Download } from "react-bootstrap-icons";
import { FiDownloadCloud } from "react-icons/fi";

const PrauthorizationList = () => {
  const dispatch = useDispatch();
  const { preauthorizationList, preauthorizationFilters } = useSelector(
    (state) => state.preauthorization
  );

  const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));
  const [totalRows, setTotalRows] = useState(0);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { allPermissionType } = useSelector((state) => state.role);
  const preauthorizationSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) => item?.rolemngmttyp_name === "Preauthorization"
  );
  const { control, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      payer: "",
      start_date: getToday(),
      end_date: getToday(),
      insurance: "",
      patient_id: "",
      patient_det: "",
      reference_no_ref: "",
      preauthorization_type: "",
      preauthid: '',
      preauthorization_sub_type: "",
      identity_type: "",
      identity_number: "",
      preauth_status: "",
      pat_det: "",
      second_response: false,
      communication: false,
      advance_preauthorization: false,
    },
  });

  useEffect(() => {
    dispatch(handleResetAll({ check: "reset" }));
    dispatch(handleResetCreateClaim());
    dispatch(handlePreauthFormData({ check: "reset" }));
    dispatch(handleResetPatientSearch());
  }, []);

  const requestBody = (customPage = page, customLimit = rowsPerPage) => ({
    branch_id: selectedBranch?.value || selectedBranch?.branch_id || 1,
    payer_id: preauthorizationFilters?.payer?.payer_id || "",
    insurance_company_id:
      preauthorizationFilters?.insurance?.insurance_company_id || "",
    claim_type:
      preauthorizationFilters?.preauthorization_type?.claim_types_id || "",
    claimsub_types_name:
      preauthorizationFilters?.preauthorization_sub_type?.claimsub_types_id ||
      "",
    ref_number: preauthorizationFilters?.reference_number || "",
    preauthid: preauthorizationFilters?.preauthid || "",
    frm_dte: preauthorizationFilters?.start_date
      ? moment(preauthorizationFilters?.start_date).format("DD-MM-YYYY")
      : "",
    to_dte: preauthorizationFilters?.end_date
      ? moment(preauthorizationFilters?.end_date).format("DD-MM-YYYY")
      : "",
    ident_typ: preauthorizationFilters?.identity_type?.ident_type_id || "",
    pat_det: preauthorizationFilters?.patient || "",
    preauth_sts:
      preauthorizationFilters?.preauthorization_status?.allstatus_code || "",
    is_secondresponse: preauthorizationFilters?.secondary_response || false,
    is_communication: preauthorizationFilters?.communication || false,
    is_advpreauth: preauthorizationFilters?.advanced_authorization || false,
    start_point: customPage,
    limit: customLimit,
  });

  const fetchPreauthorizationList = async (body = {}) => {
    try {
      const response = await dispatch(
        GetAllPreauthorizationList(body)
      ).unwrap();
      setTotalRows(response?.total_rows || 0);
    } catch (error) {
      // toast.error("Something Went Wrong");
    }
  };

  // ✅ Filter Click Handler — resets to page 0
  const handleCheckListFilter = () => {
    const newPage = 0;
    setPage(newPage); // Reset page
    const body = requestBody(newPage, rowsPerPage);
    fetchPreauthorizationList(body);
  };

  // ✅ Pagination handlers
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePerRowsChange = useCallback((newPerPage) => {
    setRowsPerPage(newPerPage);
    setPage(0);
  }, []);

  // ✅ Reset Button Handler
  const handleReset = async () => {
    reset({
      payer: "",
      start_date: getToday(),
      end_date: getToday(),
      payer_id: "",
      insurance: "",
      patient_id: "",
      patient_det: "",
      reference_no_ref: "",
      preauthorization_type: "",
      preauthorization_sub_type: "",
      identity_type: "",
      identity_number: "",
      preauth_status: "",
      preauthid: '',
      pat_det: "",
      second_response: false,
      communication: false,
      advance_preauthorization: false,
    });

    setPage(0);
    setRowsPerPage(10);
    setResetTrigger(Math.random());

    dispatch(handleResetPreauthorizationFilter());

    const resetBody = {
      branch_id: selectedBranch?.value || 1,
      payer_id: "",
      insurance_company_id: "",
      claim_type: "",
      claimsub_types_name: "",
      ref_number: "",
      frm_dte: moment(getToday()).format("DD-MM-YYYY"),
      to_dte: moment(getToday()).format("DD-MM-YYYY"),
      ident_typ: "",
      pat_det: "",
      preauth_sts: "",
      preauthid: '',
      is_secondresponse: false,
      is_communication: false,
      is_advpreauth: false,
      start_point: 0,
      limit: 10,
    }; // Fresh filters
    await fetchPreauthorizationList(resetBody);
  };

  // ✅ Fetch when page/rowsPerPage change
  useEffect(() => {
    const body = requestBody(page, rowsPerPage);
    fetchPreauthorizationList(body);
  }, [page, rowsPerPage]);

  const handleDownloadExcel = async () => {
    const excelBody = {
      branch_id: selectedBranch?.value || selectedBranch?.branch_id || 1,
      payer_id: preauthorizationFilters?.payer?.payer_id || "",
      insurance_company_id:
        preauthorizationFilters?.insurance?.insurance_company_id || "",
      claim_type:
        preauthorizationFilters?.preauthorization_type?.claim_types_id || "",
      claimsub_types_name:
        preauthorizationFilters?.preauthorization_sub_type?.claimsub_types_id ||
        "",
      ref_number: preauthorizationFilters?.reference_number || "",
      preauthid: preauthorizationFilters?.preauthid || "",
      frm_dte: preauthorizationFilters?.start_date
        ? moment(preauthorizationFilters?.start_date).format("DD-MM-YYYY")
        : "",
      to_dte: preauthorizationFilters?.end_date
        ? moment(preauthorizationFilters?.end_date).format("DD-MM-YYYY")
        : "",
      ident_typ: preauthorizationFilters?.identity_type?.ident_type_id || "",
      pat_det: preauthorizationFilters?.patient || "",
      preauth_sts:
        preauthorizationFilters?.preauthorization_status?.allstatus_code || "",
      is_secondresponse: preauthorizationFilters?.secondary_response || false,
      is_communication: preauthorizationFilters?.communication || false,
      is_advpreauth: preauthorizationFilters?.advanced_authorization || false,
      start_point: 0,
      limit: 100000,
    };
    const data = await dispatch(GetAllPreauthorizationList(excelBody)).unwrap();

    const preauthorizationData = data?.data?.preauth_data;

    const flattenedArray =
      preauthorizationData &&
      preauthorizationData?.length > 0 &&
      preauthorizationData.map((row, index) => [
        index + 1,
        moment(
          row?.is_advpreauth
            ? row?.responsedatetime
            : row.updated_at || row.created_at
        ).format("DD-MM-YYYY , hh:mm:ss A"),
        row.responsedatetime
          ? moment(row?.updated_at || row.responsedatetime).format(
            "DD-MM-YYYY , hh:mm:ss A"
          )
          : "-" || "-",
        row?.insurance_direct === 0
          ? `${row?.payer_name} - ${row?.insurance_shname}`
          : row?.payer_name || "",
        row?.preauth_nwid || row?.preauthfull_id || "",
        row?.preauthref || "",
        row?.preauthclmtyp || "",
        row?.patient_identifier || "",
        `${row?.patient_firstname || ""} ${row?.patient_familyname || ""}`,
        row?.patient_nationalid || "",
        row?.class || "",
        row?.patient_gender || "",
        row?.patient_maritalstatus || "",
        row?.patient_occupation || "",
        row?.memberid || "",
        row?.polcyno || "",
        row?.net_amount || 0,
        row?.apprve_amount || 0,
        row?.pre_auth_status || "",
        row?.is_advpreauth ? "Yes" : "No",
        row?.practitioner_name || "",
        row?.crteam_spclty || "",
        row?.disposition || "",
        row?.req_bundleid || "",
        row?.resp_bundleid || "",
        `${row?.employees_firstname || ""} ${row?.employees_familyname || ""}`,
      ]);
    handleDownloadXLSX(
      [
        [
          "#",
          "Request Date",
          "Response Date",
          "Payer Name",
          "Preauth Id",
          "Approval Number",
          "Approval Type",
          "Patient Identifier", ///------------
          "Patient Name",
          "Patient ID",
          "Class",
          "Gender",
          "Marital Status",
          "Occupation",
          "Membership No",
          "Policy Number",
          "Net Amount",
          "Approved Amount",
          "Status",
          "Advanced PreAuth Identifier",
          "Doctor Name",
          "Speciality", // spdfiality
          "Remarks",
          "Request BundleId",
          "Response BundleId",
          "User Name",
        ],
        ...(flattenedArray || []),
      ],
      `Preauthorization_List_Export_${moment().format("DD-MM-YYYY_hh:mm:ss")}`
    );
  };

  return (
    <Form
      noValidate
      onSubmit={handleSubmit(handleCheckListFilter)}
      className="p-0"

    >
      <FalconComponentCard>
        <FalconComponentCard.Header className="d-flex fixed-header justify-content-between align-items-center">
          <Col xs="auto m-0">
            <h5 className="text-primary mb-0 fs-10">Preauthorization List</h5>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button size="sm" variant="outline-danger" onClick={handleReset}>
              Reset
            </Button>
            <Button
              type="submit"
              size="sm"
              variant="success"

            >
              Filter
            </Button>
            {Number(preauthorizationSetting?.export) === 1 && (
              <Button size="sm" variant="warning" onClick={handleDownloadExcel}>
                <FiDownloadCloud size={16} />
                Excel
              </Button>
            )}
          </Col>
        </FalconComponentCard.Header>

        <FalconComponentCard.Body
          style={{
            marginTop: "40px",
            overflowY: "auto",
            maxHeight: "calc(100vh - 200px)",
          }}
        >
          <PreauthorizationListFilter
            onSubmit={handleCheckListFilter}
            setValue={setValue}
            watch={watch}
            control={control}
            resetTrigger={resetTrigger}
          />
          <PreauthorizationListTable
            page={page}
            rowsPerPage={rowsPerPage}
            handlePageChange={handlePageChange}
            handlePerRowsChange={handlePerRowsChange}
            data={preauthorizationList}
            totalRows={totalRows}
          />
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </Form>
  );
};

export default PrauthorizationList;
